import React, {useCallback, useContext, useRef} from 'react'
import {useDispatch} from 'react-redux'
import {useSelector} from 'react-redux'
import {PriceWatchCTAWrapper} from 'components/PriceWatchCTAWrapper/PriceWatchCTAWrapper'
import {useShowDatePickerOverlay} from 'hooks/useShowOverlay/useShowOverlay'
import {hideOverlay} from 'modules/overlay/slice'
import {getHotel, getIsSearchPending} from 'modules/sapiSearch/selectors'
import {openSearchBox, setIsSearchBoxActive} from 'modules/searchBox/slice'
import {DatePickerType, SearchBoxAncestor} from 'modules/searchBox/types'

import {NoStyleLink} from '@vio/atlas/src/components/designSystem/helpers/NoStyleLink'
import {useIsEmbeddedWithBrand} from '@vio/core/src/_web/brand/hooks/useIsEmbeddedWithBrand'
import {getClosestDataIdToEventTarget} from '@vio/core/src/_web/utils/browser'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/hooks/useDispatchTeamEvent'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import {PriceWatchBell} from '@vio/shared/src/priceWatch/components/PriceWatchBell'
import {PriceWatchDetailedToggle} from '@vio/shared/src/priceWatch/components/PriceWatchDetailedToggle'

import {useHotelCardActions} from '../../../hooks/useHotelCardActions'
import {useOtaMode} from '../../../hooks/useOtaMode'
import {useShowAccommodationPage} from '../../../hooks/useShowAccommodationPage'
import {SelectedElement} from '../../../utils/selectedElement'
import {HotelCardContext} from '../../HotelCardContext'
import {BoVioFirstCardGallery} from './HotelCardGallery'
import {BoVioFirstCardLayout} from './HotelCardLayout'
import {BoVioFirstOfferDetails} from './OfferDetails/BoVioFirstOfferDetails'
import {PropertyDetails} from './PropertyDetails'

interface Props {
  isPriorityCard: boolean
}
export const ExperimentalBoVioFirstCard: React.FC<Props> = ({
  isPriorityCard
}) => {
  const dispatch = useDispatch()
  const {hotelId, isAnchor} = useContext(HotelCardContext)
  const {isOTAMode} = useOtaMode()
  const {onShowHotelDetails, trackUserHotelCardClicked} = useHotelCardActions()
  const dispatchTeamEvent = useDispatchTeamEvent()

  const showDatePickerOverlay = useShowDatePickerOverlay()
  const hotel = useSelector(state => getHotel(state, hotelId))
  const isSearchPending = useSelector(getIsSearchPending)

  const isEmbedded = useIsEmbeddedWithBrand()
  const {getAccommodationPageUrl} = useShowAccommodationPage()
  const accommodationPageURL = getAccommodationPageUrl(hotelId)
  const anchorRef = useRef<HTMLAnchorElement>(null)

  const handleOnClick = useCallback(
    (selectedElement: string, e?: React.MouseEvent) => {
      if (!hotelId) return

      trackUserHotelCardClicked({
        hotelId,
        component: 'ExperimentalBoVioFirstCardLayout',
        selectedElement
      })

      // Let the anchor handle navigation
      if (anchorRef.current?.contains(e?.target as Node)) return

      onShowHotelDetails(hotelId)
    },
    [hotelId, trackUserHotelCardClicked, onShowHotelDetails]
  )

  const handleHotelCardClick = useCallback(
    (e: React.MouseEvent) => {
      const selectedElement =
        getClosestDataIdToEventTarget(e) || SelectedElement.BlankSpace

      handleOnClick(selectedElement, e)
    },
    [handleOnClick]
  )
  const handleGalleryClick = useCallback(() => {
    handleOnClick('ExperimentalBoVioFirstCardGallery')
  }, [handleOnClick])

  const handleChangeDatesClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      dispatchTeamEvent(
        trackEvent({
          category: Category.User,
          entity: Entity.SoldOutChangeDatesButton,
          action: Action.Clicked
        })
      )
      dispatch(hideOverlay())
      // The mobile date picker is mounted only while the search box is open
      dispatch(openSearchBox(SearchBoxAncestor.Page))
      dispatch(setIsSearchBoxActive(true))
      showDatePickerOverlay(DatePickerType.CheckIn)
    },
    [dispatchTeamEvent, dispatch, showDatePickerOverlay]
  )

  return (
    <BoVioFirstCardLayout
      gallerySlot={
        <BoVioFirstCardGallery
          isPriorityCard={isPriorityCard}
          onClick={handleGalleryClick}
        />
      }
      galleryButtonSlot={
        <PriceWatchCTAWrapper
          hotel={hotel}
          ctaComponent={PriceWatchBell}
          componentName="PriceWatchBell"
          searchIsCompleted={!isSearchPending}
        />
      }
      propertyDetailsSlot={
        <NoStyleLink
          ref={anchorRef}
          href={accommodationPageURL}
          rel="noopener noreferrer"
          target={isEmbedded ? '_self' : '_blank'}
          tabIndex={0}
        >
          <PropertyDetails />
        </NoStyleLink>
      }
      offerSlot={
        <BoVioFirstOfferDetails
          hotelId={hotelId}
          isAnchor={isAnchor}
          onChangeDatesClick={handleChangeDatesClick}
          onClick={handleOnClick}
        />
      }
      priceWatchSlot={
        isAnchor && (
          <>
            <PriceWatchCTAWrapper
              hotel={hotel}
              ctaComponent={PriceWatchDetailedToggle}
              componentName="PriceWatchDetailedToggle"
              searchIsCompleted
            />
          </>
        )
      }
      isAnchor={isAnchor}
      isOTAMode={isOTAMode}
      onClick={handleHotelCardClick}
    />
  )
}
